import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaymentAlerts } from '../hooks/usePaymentAlerts';
import './PaymentAlerts.css';

const PaymentAlerts = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { overdue, dueSoon, totalAlerts, isLoading } = usePaymentAlerts();

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const calculateDaysUntilDue = (dueDate) => {
    // Parsear fecha de vencimiento
    let dueDateStr;
    if (typeof dueDate === 'string') {
      dueDateStr = dueDate.split('T')[0]; // Extraer solo la fecha si viene con hora
    } else {
      dueDateStr = new Date(dueDate).toISOString().split('T')[0];
    }
    
    // Comparar con hoy usando strings para evitar problemas de zona horaria
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const todayDate = new Date(todayStr + 'T00:00:00');
    const dueDateParsed = new Date(dueDateStr + 'T00:00:00');
    const diffTime = dueDateParsed - todayDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Usar floor en lugar de ceil
    return diffDays;
  };

  if (isLoading || totalAlerts === 0) {
    return null;
  }

  return (
    <div className="payment-alerts-container" ref={dropdownRef}>
      <button
        className="payment-alerts-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Alertas de pagos"
      >
        <span className="payment-alerts-icon">🔔</span>
        {totalAlerts > 0 && (
          <span className="payment-alerts-badge">{totalAlerts}</span>
        )}
      </button>

      {isOpen && (
        <div className="payment-alerts-dropdown">
          <div className="payment-alerts-header">
            <h3>Alertas de Pagos</h3>
            <button
              className="payment-alerts-close"
              onClick={() => setIsOpen(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>

          <div className="payment-alerts-content">
            {/* Pagos Vencidos */}
            {overdue.length > 0 && (
              <div className="payment-alerts-section">
                <div className="payment-alerts-section-header overdue">
                  <span className="alert-icon">⚠️</span>
                  <h4>Pagos Vencidos ({overdue.length})</h4>
                </div>
                <div className="payment-alerts-list">
                  {overdue.slice(0, 5).map((payment) => {
                    const remaining = parseFloat(payment.amount) - parseFloat(payment.paid_amount || 0);
                    return (
                      <div
                        key={payment.id}
                        className="payment-alert-item overdue"
                        onClick={() => {
                          navigate(`/payments?status=overdue`);
                          setIsOpen(false);
                        }}
                      >
                        <div className="alert-item-info">
                          <strong>
                            {payment.student?.full_name || 
                             payment.enrollment?.student?.full_name || 
                             `Matrícula #${payment.enrollment_id}`}
                          </strong>
                          <span className="alert-item-date">
                            Vence: {formatDate(payment.due_date)}
                          </span>
                        </div>
                        <div className="alert-item-amount">
                          {formatCurrency(remaining)}
                        </div>
                      </div>
                    );
                  })}
                  {overdue.length > 5 && (
                    <button
                      className="alert-view-all"
                      onClick={() => {
                        navigate('/payments?status=overdue');
                        setIsOpen(false);
                      }}
                    >
                      Ver todos los pagos vencidos ({overdue.length})
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Pagos Próximos a Vencer */}
            {dueSoon.length > 0 && (
              <div className="payment-alerts-section">
                <div className="payment-alerts-section-header due-soon">
                  <span className="alert-icon">⏰</span>
                  <h4>Próximos a Vencer ({dueSoon.length})</h4>
                </div>
                <div className="payment-alerts-list">
                  {dueSoon.slice(0, 5).map((payment) => {
                    const remaining = parseFloat(payment.amount) - parseFloat(payment.paid_amount || 0);
                    const daysUntil = calculateDaysUntilDue(payment.due_date);
                    // No mostrar si ya está vencido (días negativos) o si vence hoy (0 días)
                    if (daysUntil < 0) return null; // Ya vencido, no mostrar aquí
                    const daysText = daysUntil === 0 
                      ? 'Vence hoy' 
                      : `Vence en ${daysUntil} día${daysUntil !== 1 ? 's' : ''}`;
                    return (
                      <div
                        key={payment.id}
                        className="payment-alert-item due-soon"
                        onClick={() => {
                          navigate(`/payments?status=pending`);
                          setIsOpen(false);
                        }}
                      >
                        <div className="alert-item-info">
                          <strong>
                            {payment.student?.full_name || 
                             payment.enrollment?.student?.full_name || 
                             `Matrícula #${payment.enrollment_id}`}
                          </strong>
                          <span className="alert-item-date">
                            {daysText}: {formatDate(payment.due_date)}
                          </span>
                        </div>
                        <div className="alert-item-amount">
                          {formatCurrency(remaining)}
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                  {dueSoon.length > 5 && (
                    <button
                      className="alert-view-all"
                      onClick={() => {
                        navigate('/payments?status=pending');
                        setIsOpen(false);
                      }}
                    >
                      Ver todos los próximos a vencer ({dueSoon.length})
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="payment-alerts-footer">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                navigate('/payments');
                setIsOpen(false);
              }}
            >
              Ver Todos los Pagos
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentAlerts;

